import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import {
  GridComponent,
  GridDataResult,
  PageChangeEvent,
  DataStateChangeEvent,
  MultipleSortSettings,
  GridModule,
  ExcelModule,
} from '@progress/kendo-angular-grid';
import {
  GroupDescriptor,
  State,
  process,
  SortDescriptor,
} from '@progress/kendo-data-query';
import { UntypedFormGroup, FormGroup, FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { RoleDialogComponent } from './role-dialog/role-dialog.component';
import { HistoryReportDialogComponent } from './history-report-dialog/history-report-dialog.component';
import { RightDialogComponent } from './right-dialog/right-dialog.component';
import { RolesRightsService } from './roles-rights.service';
import cloneDeep from 'lodash-es/cloneDeep';
import { Platform } from '@angular/cdk/platform';
import { saveAs } from '@progress/kendo-file-saver';
import { EnvironmentService } from '../../services/environment.service';
import { ReportingService } from '../../../app/reporting/reporting.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ColumnSetting, Right, Role } from '../../models/admin-models';
import { NgxLoadingModule } from 'ngx-loading';
import { UtilityToolbarComponent } from '../../shared-components/utility-toolbar/utility-toolbar.component';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';

const createFormGroup = dataItem => {
  return new UntypedFormGroup({
    name: new FormControl(dataItem['name']),
  });
};

@Component({
  selector: 'app-roles-rights',
  templateUrl: './roles-rights.component.html',
  styleUrls: ['./roles-rights.component.scss'],
  imports: [
    NgxLoadingModule,
    UtilityToolbarComponent,
    GridModule,
    MatIconModule,
    ExcelModule,
    MatTooltipModule,
    MatButtonModule,
  ],
})
export class RolesRightsComponent implements OnInit {
  private ref = inject(ChangeDetectorRef);
  dialog = inject(MatDialog);
  private rrs = inject(RolesRightsService);
  private pltfrm = inject(Platform);
  private envSvc = inject(EnvironmentService);
  private rptSvc = inject(ReportingService);
  private snacky = inject(MatSnackBar);

  @ViewChild(GridComponent, { static: false }) private grid: GridComponent;
  @ViewChild('roleFilter', { static: false }) private roleFilter: FormControl;

  public roles: Array<Role> = [];
  public rights: Array<Right> = [];
  public parentRights: Array<Right> = [];
  public columns: ColumnSetting[] = [];
  private editedRowIndex: number;
  public pageSize = 500;
  public skip = 0;
  public gridView: GridDataResult;
  public selectedRole = ['all'];

  public groups: GroupDescriptor[] = [];
  public view: any[];
  public formGroup: FormGroup;
  private isNew: boolean;
  private environment;
  private baseUrl: string;

  public sortSettings: MultipleSortSettings = {
    mode: 'multiple',
    initialDirection: 'desc',
    allowUnsort: true,
  };

  public sort: SortDescriptor[] = [];

  public state: State = {
    skip: 0,
    take: this.pageSize,
    filter: {
      logic: 'and',
      filters: [{ field: 'name', operator: 'contains', value: '' }],
    },
  };

  loading = false;

  ngOnInit() {
    this.environment = this.envSvc.get();
    this.baseUrl = this.environment.apiBase;
    this.loading = true;
    this.rrs.getRoles().subscribe((resp: { roles: Role[] }) => {
      this.roles = resp['roles'];
      this.roles.map(role => {
        const filteredRights = role['rights'].filter(
          right => right !== '' && right !== undefined
        );
        role['rightsString'] = filteredRights.join(', ');
      });
      this.rrs
        .getAllRights()
        .subscribe((resp: { rights: Right[]; parentRight: Right[] }) => {
          this.rights = resp['rights'].filter(
            x => x['parentid'] !== 'Application' && x['parentid'] !== null
          );
          this.parentRights = resp['parentRight'];
          this.loading = false;
          this.drawGrid();
        });
    });
  }

  click(right: Right, column: ColumnSetting) {
    right[column.field] = !right[column.field];
    if (right[column.field]) {
      this.rrs.addRoleRight(column.field, right.id).subscribe({
        next: res => {
          if (res['result'] === false) {
            right[column.field] = !right[column.field];
          }
        },
        error: () => {
          right[column.field] = !right[column.field];
        },
      });
    } else {
      this.rrs.removeRoleRight(column.field, right.id).subscribe({
        next: res => {
          if (res['result'] === false) {
            right[column.field] = !right[column.field];
          }
        },
        error: () => {
          right[column.field] = !right[column.field];
        },
      });
    }
  }

  public dataStateChange(state: DataStateChangeEvent): void {
    if (!this.state.sort) {
      this.state.sort = state.sort;
    } else if (
      this.state.sort.length > 0 &&
      this.state.sort[0] !== undefined &&
      !this.state.sort.find(sortData => sortData.field === state.sort[0].field)
    ) {
      this.state.sort.push(state.sort[0]);
    } else if (
      this.state.sort.length > 0 &&
      this.state.sort[0] !== undefined &&
      this.state.sort.find(sortData => sortData.field === state.sort[0].field)
    ) {
      this.state.sort.map((sortData, i) => {
        if (sortData.field === state.sort[0].field && sortData.dir === 'desc') {
          sortData.dir = 'asc';
        } else if (
          sortData.field === state.sort[0].field &&
          sortData.dir === 'asc'
        ) {
          this.state.sort.splice(i, 1);
        }
      });
    }

    this.state.filter = state.filter;
    this.state.skip = state.skip;
    this.state.take = state.take;
    this.gridView = process(this.roles, this.state);
  }

  openRoleDialog(name: string, addRole = false) {
    const role = this.roles.find(r => r.name === name);
    const dialogRef = this.dialog.open(RoleDialogComponent, {
      panelClass: 'role-dialog',
      data: {
        rights: this.rights,
        role: role || {
          id: '',
          name: '',
          rights: [],
          rightsString: '',
        },
        headline: 'Add Role',
        addRole,
      },
      width: '90vw',
      maxWidth: '90vw',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const rolePayload = {
          roleId: result['data']['id'],
          roleName: result['data']['name'],
          rights: {
            add: result['add'],
            drop: result['drop'],
          },
        };

        if (result.result === 'add') {
          result['data']['rights'] = result['data']['assignedRights'];
          this.roles.push(result['data']);
          this.rrs.addRole(rolePayload).subscribe({
            next: () => {
              this.snacky.open(
                `${rolePayload.roleName} was created.`,
                'Close',
                {
                  duration: 4000,
                }
              );
              this.gridView = process(this.roles, this.state);
            },
            error: error => {
              console.error(error);
              this.snacky.open(
                `There was an error. ${rolePayload.roleName} could not be updated.`,
                'Close',
                {
                  duration: 4000,
                }
              );
            },
          });
        } else if (result.result === 'update') {
          role['name'] = result['data']['name'];
          role['id'] = result['data']['id'];
          this.rrs.editRole(rolePayload).subscribe({
            next: () => {
              this.drawGrid();
              this.gridView = process(this.roles, this.state);
              this.snacky.open(
                `${rolePayload.roleName} was updated.`,
                'Close',
                {
                  duration: 4000,
                }
              );
            },
            error: error => {
              console.error(error);
              this.snacky.open(
                `There was an error. ${rolePayload.roleName} could not be updated.`,
                'Close',
                {
                  duration: 4000,
                }
              );
            },
          });
        } else if (result.result === 'delete') {
          this.roles.splice(this.roles.indexOf(role));
          this.rrs.removeRole(result['data']['id']).subscribe({
            next: () => {
              this.drawGrid();
              this.gridView = process(this.roles, this.state);
              this.snacky.open(
                `${rolePayload.roleName} was deleted.`,
                'Close',
                {
                  duration: 4000,
                }
              );
            },
            error: error => {
              console.error(error);
              this.snacky.open(
                `There was an error. ${rolePayload.roleName} could not be updated.`,
                'Close',
                {
                  duration: 4000,
                }
              );
            },
          });
        } else {
          this.gridView = process(this.roles, this.state);
        }
      }
    });
  }

  openRightDialog(dataItem = null) {
    const dialogRef = this.dialog.open(RightDialogComponent, {
      panelClass: 'right-dialog',
      width: '90vw',
      maxWidth: '90vw',
      data: {
        right: dataItem,
        headline: 'Add Right',
        rights: this.parentRights.map(i => i.id),
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        switch (result.result) {
          case 'add':
            this.rights.push(result['data']);
            this.rrs
              .addRight(
                result['data']['id'],
                result['data']['name'],
                result['data']['parentid']
              )
              .subscribe({
                next: () => (this.gridView = process(this.roles, this.state)),
                error: error => console.error(error),
              });
            break;
          case 'update':
            dataItem['name'] = result['data']['name'];
            dataItem['id'] = result['data']['id'];
            dataItem['parentid'] = result['data']['parentid'];
            this.rrs
              .editRight(
                result['data']['id'],
                result['data']['name'],
                result['data']['parentid']
              )
              .subscribe({
                next: () => {
                  this.drawGrid();
                  this.gridView = process(this.roles, this.state);
                },
                error: error => console.error(error),
              });
            break;
          case 'delete':
            this.rights = this.rights.filter(item => item !== dataItem);
            this.rrs.removeRight(result['data']['id']).subscribe({
              next: () => {
                this.drawGrid();
                this.gridView = process(this.roles, this.state);
              },
              error: error => console.error(error),
            });
            break;
          default:
            this.gridView = process(this.roles, this.state);
            break;
        }
      }
    });
  }

  downloadHistoryReport() {
    const dialogRef = this.dialog.open(HistoryReportDialogComponent, {
      panelClass: 'right-dialog',
      data: {},
    });
    dialogRef.afterClosed().subscribe(resp => {
      this.loading = true;
      if (resp && resp.result === 'download') {
        const start = new Date(resp.data.startDate).toISOString();
        const end = new Date(resp.data.endDate).toISOString();
        this.rptSvc.getRoleRightHistoryReport(start, end).subscribe(rep => {
          const date = new Date();
          const filename =
            'RoleRightHistoryReport-' +
            (date.getMonth() + 1) +
            date.getDate() +
            date.getFullYear() +
            '.pdf';
          const iosSafari =
            this.pltfrm.IOS ||
            (navigator?.['userAgentData']?.platform === 'macOS' &&
              navigator.maxTouchPoints > 1);
          let fileData;
          const reader = new FileReader();
          reader.readAsDataURL(rep);
          reader.onloadend = () => {
            fileData = reader.result;
            saveAs(fileData, filename, {
              proxyURL: `${this.baseUrl}/util/proxy`,
              proxyTarget: '_self',
              forceProxy: iosSafari,
            });
            this.loading = false;
          };
        });
      } else {
        this.loading = false;
      }
    });
  }

  drawGrid() {
    this.columns = [];
    this.roles.forEach(role => {
      if (
        this.selectedRole.includes('all') ||
        this.selectedRole.includes(role.id)
      ) {
        this.columns.push({
          title: role.name,
          field: role.id,
          role: role,
        });
        this.rights.forEach(right => {
          right[role.id] = role.rights.indexOf(right.id) >= 0;
        });
      }
    });
    this.loadItems();
    this.ref.detectChanges();
  }

  addHandler() {
    this.formGroup = createFormGroup({
      name: '',
    });
    this.isNew = true;
    this.grid.addRow(this.formGroup);
  }

  public editClick({ isEdited, dataItem, rowIndex }): void {
    if (isEdited || (this.formGroup && !this.formGroup.valid)) {
      return;
    }

    if (this.isNew) {
      rowIndex += 1;
    }

    this.saveCurrent();

    this.formGroup = createFormGroup(dataItem);
    this.editedRowIndex = rowIndex;

    this.grid.editRow(rowIndex, this.formGroup);
  }

  private saveCurrent(): void {
    if (this.formGroup) {
      this.rights[this.editedRowIndex]['name'] =
        this.formGroup.controls['name'].value;
      this.loadItems();
      this.closeEditor();
    }
  }

  private closeEditor(): void {
    this.grid.closeRow(this.editedRowIndex);

    this.isNew = false;
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.loadItems();
  }

  private loadItems(): void {
    this.gridView = {
      data: this.roles.slice(this.skip, this.skip + this.pageSize),
      total: this.roles.length,
    };
  }

  private toggleAllRoles(): void {
    this.selectedRole =
      this.selectedRole.length < this.roles.length
        ? ['all', ...this.roles.map(x => x.id)]
        : [];
    this.drawGrid();
    this.gridView = process(this.roles, this.state);
  }

  exportToExcel(grid: GridComponent): void {
    const newGrid = grid;
    newGrid.saveAsExcel();
  }

  public excelData = () => {
    const fullData = cloneDeep(this.rights);

    const output = process(fullData, {});
    return output;
  };

  sortIcon(field) {
    if (
      this.state.sort &&
      this.state.sort.find(sortData => sortData.field === field) &&
      this.state.sort.find(sortData => sortData.field === field).dir === 'asc'
    ) {
      return 'arrow_upward';
    } else if (
      this.state.sort &&
      this.state.sort.find(sortData => sortData.field === field) &&
      this.state.sort.find(sortData => sortData.field === field).dir === 'desc'
    ) {
      return 'arrow_downward';
    }
    return '';
  }

  sortOrder(field) {
    if (
      this.state.sort &&
      this.state.sort.findIndex(sortData => sortData.field === field) > -1
    ) {
      return (
        this.state.sort.findIndex(sortData => sortData.field === field) + 1
      );
    }
    return '';
  }
}
